import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiTrash2, FiChevronDown, FiChevronUp, FiSend, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { submitClaim, errMsg } from '../../utils/expenseApi';

const CATEGORIES = [
  { value: 'TRAVEL_AIRFARE',   label: 'Airfare' },
  { value: 'TRAVEL_HOTEL',     label: 'Hotel' },
  { value: 'TRAVEL_GROUND',    label: 'Ground Transport' },
  { value: 'TRAVEL_TRAIN',     label: 'Train' },
  { value: 'MEALS',            label: 'Meals' },
  { value: 'MEALS_CLIENT',     label: 'Client Meals' },
  { value: 'OFFICE_SUPPLIES',  label: 'Office Supplies' },
  { value: 'COMMUNICATION',    label: 'Communication' },
  { value: 'CONFERENCE',       label: 'Conference' },
  { value: 'FUEL',             label: 'Fuel' },
  { value: 'VISA_PASSPORT',    label: 'Visa / Passport' },
  { value: 'MEDICAL',          label: 'Medical' },
  { value: 'MISCELLANEOUS',    label: 'Miscellaneous' },
];

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'JPY', 'CAD', 'AUD'];

const emptyLine = (n) => ({
  line_number: n,
  expense_category: '',
  expense_date: '',
  vendor_name: '',
  country: '',
  city: '',
  amount: '',
  tax_amount: '',
  amount_local: '',
  exchange_rate: '',
  cost_centre: '',
  wbs_element: '',
  internal_order: '',
  gl_account: '',
  justification: '',
  is_policy_exception: false,
  item_text: '',
  _expanded: false,
});

export default function SubmitClaim({ onSuccess }) {
  const [header, setHeader] = useState({
    company_code: '1000',
    cost_centre: '',
    currency_code: 'INR',
    description: '',
  });
  const [lines,      setLines]      = useState([emptyLine(1)]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null);

  const updateHeader = (k, v) => setHeader(h => ({ ...h, [k]: v }));
  const updateLine   = (idx, k, v) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine      = () => setLines(ls => [...ls, emptyLine(ls.length + 1)]);
  const removeLine   = (idx) => {
    if (lines.length === 1) return;
    setLines(ls => ls.filter((_, i) => i !== idx).map((l, i) => ({ ...l, line_number: i + 1 })));
  };
  const toggleExpand = (idx) => updateLine(idx, '_expanded', !lines[idx]._expanded);

  const validate = () => {
    if (!header.company_code.trim()) return 'Company code is required.';
    if (!header.cost_centre.trim())  return 'Cost centre is required.';
    for (const l of lines) {
      if (!l.expense_category) return `Line ${l.line_number}: select a category.`;
      if (!l.expense_date)     return `Line ${l.line_number}: date is required.`;
      if (!l.amount || parseFloat(l.amount) <= 0) return `Line ${l.line_number}: amount must be > 0.`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }

    const payload = {
      company_code:  header.company_code.trim(),
      cost_centre:   header.cost_centre.trim(),
      currency_code: header.currency_code.toUpperCase(),
      description:   header.description.trim() || undefined,
      lines: lines.map(({ _expanded, ...l }) => {
        const line = {
          line_number:      l.line_number,
          expense_category: l.expense_category,
          expense_date:     l.expense_date,
          amount:           parseFloat(l.amount),
        };
        if (l.vendor_name.trim())  line.vendor_name = l.vendor_name.trim();
        if (l.country.trim())      line.country = l.country.trim().toUpperCase();
        if (l.city.trim())         line.city = l.city.trim();
        if (l.tax_amount)          line.tax_amount = parseFloat(l.tax_amount);
        if (l.amount_local)        line.amount_local = parseFloat(l.amount_local);
        if (l.exchange_rate)       line.exchange_rate = parseFloat(l.exchange_rate);
        if (l.cost_centre.trim())  line.cost_centre = l.cost_centre.trim();
        if (l.wbs_element.trim())  line.wbs_element = l.wbs_element.trim();
        if (l.internal_order.trim()) line.internal_order = l.internal_order.trim();
        if (l.gl_account.trim())   line.gl_account = l.gl_account.trim();
        if (l.justification.trim()) line.justification = l.justification.trim();
        if (l.is_policy_exception)  line.is_policy_exception = true;
        if (l.item_text.trim())    line.item_text = l.item_text.trim();
        return line;
      }),
    };

    setSubmitting(true);
    try {
      const res = await submitClaim(payload);
      setSubmitted(res);
      toast.success('Claim submitted! Policy check initiated.');
    } catch (err) {
      toast.error(errMsg(err, 'Failed to submit claim.'));
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setSubmitted(null);
    setHeader({ company_code: '1000', cost_centre: '', currency_code: 'INR', description: '' });
    setLines([emptyLine(1)]);
  };

  if (submitted) {
    return (
      <motion.div className="exp-section" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
        <div className="exp-card" style={{ maxWidth: 500, margin: '0 auto', padding: '36px 32px', textAlign: 'center' }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
            style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', border: '2px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}
          >
            <FiCheckCircle size={26} color="#15803d" />
          </motion.div>
          <h2 style={{ margin: '0 0 8px', fontFamily: "'Space Grotesk',sans-serif", color: 'var(--ep-dark)', fontSize: '1.3rem' }}>
            Claim Submitted!
          </h2>
          <p style={{ margin: '0 0 6px', color: 'var(--e-muted)', fontSize: '0.9rem' }}>
            {submitted.message}
          </p>
          <p style={{ margin: '0 0 24px', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--e-muted)', background: 'var(--e-bg)', padding: '4px 10px', borderRadius: 6, display: 'inline-block' }}>
            ID: {submitted.claim_id}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <motion.button className="exp-btn primary" onClick={() => onSuccess?.(submitted.claim_id)} whileTap={{ scale: 0.96 }}>
              Track this Claim
            </motion.button>
            <motion.button className="exp-btn" onClick={resetForm} whileTap={{ scale: 0.96 }}>
              Submit Another
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  const totalAmount = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  return (
    <motion.div className="exp-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <h2 className="exp-section-title">Submit Expense Claim</h2>

      <form onSubmit={handleSubmit}>
        {/* ── Header ── */}
        <div className="exp-card" style={{ marginBottom: 14 }}>
          <div className="exp-card-header">
            <span className="exp-card-title">Claim Details</span>
          </div>
          <div className="exp-card-body">
            <div className="exp-form-grid">
              <div className="exp-field">
                <label className="exp-label">Company Code *</label>
                <input className="exp-input" value={header.company_code} onChange={e => updateHeader('company_code', e.target.value)} required maxLength={10} placeholder="1000" />
              </div>
              <div className="exp-field">
                <label className="exp-label">Cost Centre *</label>
                <input className="exp-input" value={header.cost_centre} onChange={e => updateHeader('cost_centre', e.target.value)} required maxLength={20} placeholder="CC001" />
              </div>
              <div className="exp-field">
                <label className="exp-label">Currency *</label>
                <select className="exp-select" value={header.currency_code} onChange={e => updateHeader('currency_code', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="exp-field exp-field-full">
                <label className="exp-label">Description</label>
                <input className="exp-input" value={header.description} onChange={e => updateHeader('description', e.target.value)} placeholder="e.g. Business trip to Delhi — client meeting" />
              </div>
            </div>
          </div>
        </div>

        {/* ── Expense Lines ── */}
        <div className="exp-card" style={{ marginBottom: 14 }}>
          <div className="exp-card-header">
            <span className="exp-card-title">Expense Lines</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {totalAmount > 0 && (
                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--ep)', fontFamily: "'Space Grotesk',sans-serif" }}>
                  Total: {totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })} {header.currency_code}
                </span>
              )}
              <motion.button type="button" className="exp-btn small primary" onClick={addLine} whileTap={{ scale: 0.96 }}>
                <FiPlus size={13} /> Add Line
              </motion.button>
            </div>
          </div>
          <div className="exp-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <AnimatePresence>
              {lines.map((line, idx) => (
                <motion.div
                  key={`line-${idx}`}
                  className="exp-line-card"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6, height: 0, marginBottom: 0, padding: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="exp-line-header">
                    <span className="exp-line-num">Line {line.line_number}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="exp-icon-btn" onClick={() => toggleExpand(idx)} title="Advanced fields">
                        {line._expanded ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
                      </button>
                      {lines.length > 1 && (
                        <button type="button" className="exp-icon-btn danger" onClick={() => removeLine(idx)} title="Remove">
                          <FiTrash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Required fields */}
                  <div className="exp-form-grid">
                    <div className="exp-field">
                      <label className="exp-label">Category *</label>
                      <select className="exp-select" value={line.expense_category} onChange={e => updateLine(idx, 'expense_category', e.target.value)} required>
                        <option value="">Select…</option>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="exp-field">
                      <label className="exp-label">Date *</label>
                      <input type="date" className="exp-input" value={line.expense_date} onChange={e => updateLine(idx, 'expense_date', e.target.value)} required />
                    </div>
                    <div className="exp-field">
                      <label className="exp-label">Amount * ({header.currency_code})</label>
                      <input type="number" className="exp-input" value={line.amount} onChange={e => updateLine(idx, 'amount', e.target.value)} required min="0.01" step="0.01" placeholder="0.00" />
                    </div>
                    <div className="exp-field">
                      <label className="exp-label">Vendor</label>
                      <input className="exp-input" value={line.vendor_name} onChange={e => updateLine(idx, 'vendor_name', e.target.value)} placeholder="e.g. IndiGo" />
                    </div>
                  </div>

                  {/* Advanced fields */}
                  <AnimatePresence>
                    {line._expanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="exp-form-grid" style={{ marginTop: 10 }}>
                          <div className="exp-field">
                            <label className="exp-label">Country (ISO)</label>
                            <input className="exp-input" value={line.country} onChange={e => updateLine(idx, 'country', e.target.value)} placeholder="IND" maxLength={3} />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">City</label>
                            <input className="exp-input" value={line.city} onChange={e => updateLine(idx, 'city', e.target.value)} placeholder="Mumbai" />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">Tax Amount</label>
                            <input type="number" className="exp-input" value={line.tax_amount} onChange={e => updateLine(idx, 'tax_amount', e.target.value)} min="0" step="0.01" placeholder="0.00" />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">Local Amount</label>
                            <input type="number" className="exp-input" value={line.amount_local} onChange={e => updateLine(idx, 'amount_local', e.target.value)} min="0" step="0.01" placeholder="0.00" />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">Exchange Rate</label>
                            <input type="number" className="exp-input" value={line.exchange_rate} onChange={e => updateLine(idx, 'exchange_rate', e.target.value)} min="0" step="0.0001" placeholder="1.0000" />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">Cost Centre</label>
                            <input className="exp-input" value={line.cost_centre} onChange={e => updateLine(idx, 'cost_centre', e.target.value)} placeholder="Line-level override" />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">WBS Element</label>
                            <input className="exp-input" value={line.wbs_element} onChange={e => updateLine(idx, 'wbs_element', e.target.value)} />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">Internal Order</label>
                            <input className="exp-input" value={line.internal_order} onChange={e => updateLine(idx, 'internal_order', e.target.value)} />
                          </div>
                          <div className="exp-field">
                            <label className="exp-label">GL Account</label>
                            <input className="exp-input" value={line.gl_account} onChange={e => updateLine(idx, 'gl_account', e.target.value)} />
                          </div>
                          <div className="exp-field exp-field-full">
                            <label className="exp-label">Justification</label>
                            <input className="exp-input" value={line.justification} onChange={e => updateLine(idx, 'justification', e.target.value)} placeholder="Business justification for this expense" />
                          </div>
                          <div className="exp-field exp-field-full">
                            <label className="exp-label">Item Text (max 500 chars)</label>
                            <input className="exp-input" value={line.item_text} onChange={e => updateLine(idx, 'item_text', e.target.value)} maxLength={500} placeholder="Additional description for SAP" />
                          </div>
                          <div className="exp-field">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={line.is_policy_exception}
                                onChange={e => updateLine(idx, 'is_policy_exception', e.target.checked)}
                              />
                              <span className="exp-label" style={{ margin: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.85rem', fontWeight: 600 }}>
                                Request policy exception
                              </span>
                            </label>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <motion.button
            type="submit"
            className="exp-btn primary"
            disabled={submitting}
            whileTap={{ scale: 0.96 }}
            style={{ minWidth: 140 }}
          >
            {submitting
              ? <><div className="exp-inline-spinner" /> Submitting…</>
              : <><FiSend size={14} /> Submit Claim</>
            }
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
}
